/**
 * The cabinet, on the glass.
 *
 * A stick in the bottom-left and a six-button cluster in the bottom-right,
 * absolutely positioned over the stage so they come along into fullscreen.
 *
 * Where they land depends on whether there is a letterbox. Windowed, the stage
 * reserves a band underneath the picture (`.stage[data-touch='on']`) and the pad
 * fills the stage — covering the game in order to play the game is a poor trade
 * on a screen this small. Fullscreen there is no band to reserve, so the pad is
 * fitted to the picture itself rather than to the screen: see `#fitToPicture`,
 * and note that the canvas ELEMENT in fullscreen is the whole screen while the
 * picture inside it is not.
 *
 * Three things this had to be built around, and each one shapes the file:
 *
 *   - **Which control a finger is on is re-decided on every move.** Rolling a
 *     thumb from ① to ② is how a six-button cluster is actually played, and a
 *     pad that let each control own its own pointer would swallow the roll: the
 *     second button never hears about a finger the first one is holding. So
 *     capture is only a mailbox — every event is handled once, at the root,
 *     against fresh coordinates. The cluster's cells tile with no gaps, so the
 *     roll never crosses dead space either.
 *   - **The stick keeps its pointer for the life of the press.** The opposite
 *     rule, and for the opposite reason: a thumb dragged past the ring must go
 *     on steering rather than be handed to whatever is under it.
 *   - **The stick floats.** It is drawn where it rests, but a press anywhere in
 *     its zone moves it under the thumb. Asking someone to find a 120px circle
 *     without looking away from a fight is asking them to lose the fight.
 *
 * What it drives is `LocalControls.setTouchMask` — the same latch the keyboard
 * and the gamepad reach, for whichever port this peer was given. There is
 * nothing to configure: an on-screen button is not bound to a game button, it
 * *is* one, drawn.
 */
import type { LocalControls } from './emulator/controls.js';
import { bit } from './emulator/input.js';
import type { ButtonName } from './emulator/input.js';
import { clamp } from './emulator/bindings.js';
import { detectTouch, touchByDefault, watchTouch } from './touch.js';
import type { TouchProfile } from './touch.js';

function must<T extends Element>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing element #${id}`);
  return el as unknown as T;
}

/** Survives a reload, so a phone that turned the pad off stays turned off. */
const STORE_KEY = 'retro.touch.v1';

/**
 * How far out of centre counts as a direction at all.
 *
 * A fraction of the knob's travel, not of the base: what matters is how far the
 * thumb has moved relative to how far it *can*, which is the same number on
 * every screen size.
 */
const ENGAGE = 0.32;
/** sin(22.5°) — eight equal sectors around the gate. */
const GATE = 0.3827;
/**
 * ...and it takes less to hold a direction than to enter one.
 *
 * Without the gap, a thumb resting on a sector boundary chatters between
 * LEFT and DOWN+LEFT at the touch sample rate, and under lockstep that
 * chatter is published to everyone.
 */
const GATE_HOLD = 0.28;
/**
 * How far the knob rides from the base centre, as a fraction of its radius.
 *
 * Paired with `--tp-ring` in the stylesheet, which puts the gate lamps just
 * outside `TRAVEL + half the knob`: any closer and the knob parks on top of
 * the one lamp the player actually wanted to read.
 */
const TRAVEL = 0.5;

/** The cluster in cabinet order: B1..B3 along the top, B4..B6 underneath. */
const CLUSTER: readonly (readonly ButtonName[])[] = [
  ['B1', 'B2', 'B3'],
  ['B4', 'B5', 'B6'],
];

/** The four gate lamps, clockwise from the top. */
const DIRECTIONS: readonly ButtonName[] = ['UP', 'RIGHT', 'DOWN', 'LEFT'];

const DIRECTION_GLYPH: Readonly<Record<string, string>> = {
  UP: '▲',
  RIGHT: '▶',
  DOWN: '▼',
  LEFT: '◀',
};

/**
 * What one finger is currently doing.
 *
 * The stick remembers where it was planted, because everything it reports is
 * measured from there rather than from wherever the base happens to be drawn.
 */
type Held =
  | { readonly kind: 'stick'; readonly ox: number; readonly oy: number; readonly travel: number; mask: number }
  | { readonly kind: 'pad'; mask: number };

/**
 * One axis of the gate, with hysteresis.
 *
 * Returns at most one bit, which is the point: a diagonal that reported LEFT
 * and RIGHT together would be a direction no cabinet can produce and no game
 * knows what to do with.
 */
function axisBits(n: number, prev: number, negative: ButtonName, positive: ButtonName): number {
  const enter = (name: ButtonName): number => ((prev & bit(name)) !== 0 ? GATE_HOLD : GATE);
  if (n <= -enter(negative)) return bit(negative);
  if (n >= enter(positive)) return bit(positive);
  return 0;
}

function remember(on: boolean): void {
  try {
    localStorage.setItem(STORE_KEY, on ? '1' : '0');
  } catch {
    // Private mode or a blocked origin. The choice still holds for this tab.
  }
}

function recall(): boolean | null {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw === null ? null : raw === '1';
  } catch {
    return null;
  }
}

export class TouchControls {
  readonly #controls: LocalControls;
  readonly #root = must<HTMLElement>('touchpad');
  /** The stage, which reserves a band for the pad while it is windowed. */
  readonly #stage = must<HTMLElement>('stage');
  /** The picture the pad has to sit inside once it is fullscreen. */
  readonly #canvas = must<HTMLCanvasElement>('screen');
  readonly #stickZone: HTMLElement;
  /** Kept only to measure how much height the controls need. */
  readonly #systemZone: HTMLElement;
  readonly #stick: HTMLElement;
  readonly #knob: HTMLElement;
  /** Every element that lights up, paired with the bit that lights it. */
  readonly #lamps: ReadonlyArray<{ readonly el: HTMLElement; readonly bit: number }>;

  #profile: TouchProfile;
  #enabled: boolean;
  /** There is a picture to play. No picture, no pad, whatever the toggle says. */
  #live = false;
  #showing = false;
  #fullscreen = false;
  #pointers = new Map<number, Held>();
  #mask = 0;
  #onChange: (() => void) | null = null;

  constructor(controls: LocalControls) {
    this.#controls = controls;
    this.#profile = detectTouch();
    this.#enabled = recall() ?? touchByDefault(this.#profile);

    const built = this.#build();
    this.#stickZone = built.stickZone;
    this.#systemZone = built.systemZone;
    this.#stick = built.stick;
    this.#knob = built.knob;
    this.#lamps = built.lamps;

    // Non-passive throughout: `touch-action: none` is what actually stops the
    // page scrolling under a thumb, but preventDefault on the down is what
    // stops the long-press callout, the text selection and the synthetic
    // mouse events that would otherwise double-fire everything below.
    this.#root.addEventListener('pointerdown', this.#onDown, { passive: false });
    this.#root.addEventListener('pointermove', this.#onMove, { passive: false });
    this.#root.addEventListener('pointerup', this.#onUp);
    this.#root.addEventListener('pointercancel', this.#onUp);
    // The one that catches the OS stealing a touch mid-press — an incoming
    // call, the app switcher, a gesture the browser decided was a swipe.
    this.#root.addEventListener('lostpointercapture', this.#onUp);
    this.#root.addEventListener('contextmenu', (e) => e.preventDefault());

    /*
     * A rotation moves every control out from under whatever is touching it,
     * and the stick's origin — stored in client coordinates when the thumb
     * landed — now points somewhere arbitrary. Letting go is the only honest
     * answer.
     *
     * Deliberately NOT `resize`, which sounds like the same event and is not:
     * on a phone it fires every time the URL bar collapses or returns, and
     * dropping a held direction for that would be dropping it mid-fight. The
     * geometry a resize invalidates repairs itself anyway — buttons re-hit-test
     * on every move, and a release always arrives through pointer capture.
     */
    window.addEventListener('orientationchange', this.#releaseAll);

    /*
     * Everything that can move the picture under the pad. The observers are the
     * load-bearing pair: the box changes on rotation and on entering
     * fullscreen, and the *intrinsic* size changes when a different machine's
     * geometry arrives — Neo Geo is 304x224 where CPS is 384x224 — which
     * resizes the letterbox without resizing the element, so a ResizeObserver
     * alone would never hear about it.
     */
    new ResizeObserver(() => this.#fitToPicture()).observe(this.#canvas);
    new MutationObserver(() => this.#fitToPicture()).observe(this.#canvas, {
      attributes: true,
      attributeFilter: ['width', 'height'],
    });

    watchTouch((profile) => {
      this.#profile = profile;
      // A mouse paired to a tablet can take `available` away underneath a pad
      // that is already up, so the visibility is re-derived, not just reported.
      this.#syncVisible();
      this.#onChange?.();
    });

    this.#syncVisible();
  }

  /** Fired when the device's own answer changes — a mouse paired, a keyboard
   *  detached — so the toggle can appear or step aside without a reload. */
  onChange(fn: () => void): void {
    this.#onChange = fn;
  }

  get profile(): TouchProfile {
    return this.#profile;
  }

  /** Whether to offer the toggle at all. A touchscreen is enough for this. */
  get available(): boolean {
    return this.#profile.touch;
  }

  /**
   * On, *and* on a device where that means anything.
   *
   * The stored preference outlives the machine it was made on — same origin,
   * same localStorage, a laptop the phone's owner also uses. Without the
   * `available` half, a remembered "on" would put an arcade panel over the game
   * on a desktop and hide the only latch that could dismiss it, since that
   * latch is itself shown only where there is a touchscreen.
   */
  get enabled(): boolean {
    return this.#enabled && this.available;
  }

  /** True when the pad is actually on screen — enabled *and* there is a game. */
  get showing(): boolean {
    return this.#showing;
  }

  /** What the screen is holding right now, for `__retro`. */
  get mask(): number {
    return this.#mask;
  }

  setEnabled(on: boolean): void {
    if (this.#enabled === on) return;
    this.#enabled = on;
    remember(on);
    this.#syncVisible();
  }

  /** There is a picture on the stage, or there is not. */
  setLive(live: boolean): void {
    if (this.#live === live) return;
    this.#live = live;
    this.#syncVisible();
  }

  /**
   * The picture took the viewport, or gave it back.
   *
   * Windowed, the stage reserves a band under the picture and the pad fills the
   * stage; fullscreen, there is no band to reserve — the picture is the whole
   * stage — so the pad moves *inside* the picture instead. See `#fitToPicture`.
   */
  setFullscreen(on: boolean): void {
    if (this.#fullscreen === on) return;
    this.#fullscreen = on;
    // The controls are about to be somewhere else entirely; a finger still down
    // would be steering by a geometry that no longer exists.
    this.#releaseAll();
    this.#fitToPicture();
  }

  // -- the DOM -------------------------------------------------------------

  #build(): {
    stickZone: HTMLElement;
    systemZone: HTMLElement;
    stick: HTMLElement;
    knob: HTMLElement;
    lamps: Array<{ el: HTMLElement; bit: number }>;
  } {
    const lamps: Array<{ el: HTMLElement; bit: number }> = [];

    const stickZone = el('div', 'tp-zone tp-zone-stick');
    stickZone.dataset['zone'] = 'stick';
    const stick = el('div', 'tp-stick');
    const gate = el('div', 'tp-gate');
    for (const name of DIRECTIONS) {
      const lamp = el('i', 'tp-dir');
      lamp.dataset['dir'] = name;
      lamp.textContent = DIRECTION_GLYPH[name] ?? '';
      gate.append(lamp);
      lamps.push({ el: lamp, bit: bit(name) });
    }
    const knob = el('div', 'tp-knob');
    stick.append(gate, knob);
    stickZone.append(stick);

    // Each button is a grid cell carrying the hit target and a cap drawn
    // inside it. The cells tile, so a thumb rolled between two buttons is
    // never briefly on neither of them.
    const cluster = el('div', 'tp-zone tp-zone-cluster');
    for (const row of CLUSTER) {
      for (const name of row) {
        cluster.append(key(name, name.slice(1), 'tp-key', lamps));
      }
    }

    const system = el('div', 'tp-zone tp-zone-system');
    system.append(key('COIN', 'coin', 'tp-key tp-key-pill', lamps));
    system.append(key('START', 'start', 'tp-key tp-key-pill', lamps));

    this.#root.replaceChildren(stickZone, system, cluster);
    return { stickZone, systemZone: system, stick, knob, lamps };
  }

  // -- pointers ------------------------------------------------------------

  #onDown = (e: PointerEvent): void => {
    if (!this.#showing || this.#pointers.has(e.pointerId)) return;
    const hit = this.#hit(e.clientX, e.clientY);
    // A gap in the overlay is not ours: leave the event alone so a double tap
    // on the bare stage still reaches the fullscreen handler underneath.
    if (!hit) return;
    e.preventDefault();
    /*
     * Capture, so a finger dragged off the pad — or off the window — still
     * delivers its moves and, above all, its release. The capture target is
     * only a mailbox: which control the finger is *on* is re-decided from the
     * coordinates on every move, so capturing the element it started on
     * costs nothing and does not pin the finger to it.
     */
    if (e.target instanceof Element) {
      try {
        e.target.setPointerCapture(e.pointerId);
      } catch {
        // Refused for a pointer that has already gone. Nothing left to hold.
      }
    }
    if (hit.zone === 'stick') this.#beginStick(e);
    else this.#pointers.set(e.pointerId, { kind: 'pad', mask: bit(hit.button) });
    this.#sync();
  };

  #onMove = (e: PointerEvent): void => {
    const state = this.#pointers.get(e.pointerId);
    if (!state) return;
    e.preventDefault();
    if (state.kind === 'stick') {
      this.#moveStick(e, state);
    } else {
      const hit = this.#hit(e.clientX, e.clientY);
      // Off the cluster and onto the stick zone reads as released, not as a
      // direction: one finger, one job, decided when it landed.
      state.mask = hit && hit.zone === 'pad' ? bit(hit.button) : 0;
    }
    this.#sync();
  };

  #onUp = (e: PointerEvent): void => {
    const state = this.#pointers.get(e.pointerId);
    if (!state) return;
    this.#pointers.delete(e.pointerId);
    if (state.kind === 'stick') this.#homeStick();
    this.#sync();
  };

  #releaseAll = (): void => {
    if (this.#pointers.size === 0 && this.#mask === 0) return;
    this.#pointers.clear();
    this.#homeStick();
    this.#sync();
  };

  /**
   * What is under this point, if it is ours.
   *
   * `elementFromPoint` rather than a cached table of rectangles: it respects
   * border-radius, it costs one call however many controls there are, and it
   * cannot go stale behind a rotation or a fullscreen transition. Everything
   * decorative is `pointer-events: none`, so it never answers with a knob.
   */
  #hit(x: number, y: number): { zone: 'pad'; button: ButtonName } | { zone: 'stick' } | null {
    const el = document.elementFromPoint(x, y);
    if (!el || !this.#root.contains(el)) return null;
    const button = el.closest<HTMLElement>('[data-button]')?.dataset['button'];
    if (button) return { zone: 'pad', button: button as ButtonName };
    return el.closest('[data-zone="stick"]') ? { zone: 'stick' } : null;
  }

  // -- the stick -----------------------------------------------------------

  #beginStick(e: PointerEvent): void {
    const zone = this.#stickZone.getBoundingClientRect();
    const radius = this.#stick.offsetWidth / 2 || 1;
    // Plant it under the thumb, but never so close to an edge that half the
    // circle of travel falls outside the zone it has to be steered in.
    const inset = Math.min(radius, zone.width / 2, zone.height / 2);
    const ox = zone.left + clamp(e.clientX - zone.left, inset, zone.width - inset);
    const oy = zone.top + clamp(e.clientY - zone.top, inset, zone.height - inset);

    this.#stick.dataset['engaged'] = 'true';
    this.#stick.style.transform = offset(ox - (zone.left + zone.width / 2), oy - (zone.top + zone.height / 2));

    const state: Held = { kind: 'stick', ox, oy, travel: radius * TRAVEL, mask: 0 };
    this.#pointers.set(e.pointerId, state);
    this.#moveStick(e, state);
  }

  #moveStick(e: PointerEvent, state: Held & { kind: 'stick' }): void {
    const dx = e.clientX - state.ox;
    const dy = e.clientY - state.oy;
    const distance = Math.hypot(dx, dy);
    // The knob tracks the thumb exactly out to the ring and then stops at it,
    // so pushing harder never looks like nothing happening.
    const scale = distance > state.travel ? state.travel / distance : 1;
    this.#knob.style.transform = offset(dx * scale, dy * scale);

    if (distance === 0 || distance / state.travel < ENGAGE) {
      state.mask = 0;
      return;
    }
    const nx = dx / distance;
    const ny = dy / distance;
    state.mask = axisBits(nx, state.mask, 'LEFT', 'RIGHT') | axisBits(ny, state.mask, 'UP', 'DOWN');
  }

  /** Let the base and the knob ease back to the middle. */
  #homeStick(): void {
    delete this.#stick.dataset['engaged'];
    this.#stick.style.removeProperty('transform');
    this.#knob.style.removeProperty('transform');
  }

  // -- plumbing ------------------------------------------------------------

  /**
   * Sum every finger and publish it once.
   *
   * The aggregate is the whole truth about the screen, which is why the latch
   * is handed a whole mask rather than presses and releases: a finger the
   * browser forgot to tell us about cannot leave a bit behind in a number that
   * is recomputed from scratch.
   */
  #sync(): void {
    let mask = 0;
    for (const state of this.#pointers.values()) mask |= state.mask;
    if (mask === this.#mask) return;
    this.#mask = mask;
    for (const lamp of this.#lamps) {
      lamp.el.dataset['pressed'] = String((mask & lamp.bit) !== 0);
    }
    this.#controls.setTouchMask(mask);
  }

  #syncVisible(): void {
    const show = this.enabled && this.#live;
    if (show === this.#showing) return;
    this.#showing = show;
    this.#root.hidden = !show;
    if (show) this.#stage.dataset['touch'] = 'on';
    else delete this.#stage.dataset['touch'];
    // Turning the pad off with a direction held would leave it held forever —
    // there is no element left to receive the release.
    if (!show) this.#releaseAll();
    else this.#fitToPicture();
  }

  /**
   * Put the pad inside the picture, once there is a letterbox to be outside of.
   *
   * Fullscreen, the canvas element is the whole screen and `object-fit: contain`
   * paints the game inside it — so the element's box is emphatically NOT where
   * the picture is. A phone held sideways leaves a bar of black down each side,
   * and controls laid out against the element sit in those bars: further from
   * the thumbs than they need to be, and detached from the thing they drive.
   *
   * So the contain-fit is recomputed here and the pad is sized to the picture
   * rather than to the screen. Windowed there is no letterbox — the canvas is
   * the picture — and the stage has reserved a band underneath for the pad, so
   * the inline geometry comes off and the stylesheet's `inset: 0` takes over.
   */
  #fitToPicture(): void {
    const style = this.#root.style;
    if (!this.#showing || !this.#fullscreen) {
      for (const prop of ['left', 'top', 'width', 'height', 'right', 'bottom']) {
        style.removeProperty(prop);
      }
      return;
    }
    const box = this.#canvas.getBoundingClientRect();
    const iw = this.#canvas.width;
    const ih = this.#canvas.height;
    // Mid-transition the element can measure zero, and a core that has not
    // reported its geometry yet has no intrinsic size to fit to.
    if (!box.width || !box.height || !iw || !ih) return;

    const stage = this.#stage.getBoundingClientRect();
    const scale = Math.min(box.width / iw, box.height / ih);
    const w = iw * scale;
    const h = ih * scale;
    const top = box.top - stage.top + (box.height - h) / 2;

    /*
     * How tall the controls are, measured rather than recomputed — the band and
     * the COIN/START row are sized in `vmin` by the stylesheet and this is the
     * only honest way to ask what that came to.
     *
     * The picture is not always tall enough to spare it. Held upright, a 4:3
     * game in a tall screen is letterboxed to a shallow strip, and a pad fitted
     * strictly to it would cover most of the game with its own controls — worse
     * than the problem this method exists to solve, and further from the thumbs
     * as well, since it would sit above the empty bar at the bottom rather than
     * in it. So the controls may claim at most half the box they are in; if the
     * picture cannot spare that much, the box grows down into the letterbox
     * instead of the controls eating the picture.
     */
    const needed = this.#stickZone.offsetHeight + this.#systemZone.offsetHeight;
    const bottom = Math.max(top + h, Math.min(stage.height, top + needed * 2));

    style.left = `${box.left - stage.left + (box.width - w) / 2}px`;
    style.top = `${top}px`;
    style.width = `${w}px`;
    style.height = `${bottom - top}px`;
    // `inset: 0` in the stylesheet still supplies these, and an absolutely
    // positioned box given left, right AND width is over-constrained.
    style.right = 'auto';
    style.bottom = 'auto';
  }
}

function el(tag: string, className: string): HTMLElement {
  const node = document.createElement(tag);
  node.className = className;
  return node;
}

/** A hit cell with a cap drawn inside it. The cell is the target, not the cap. */
function key(
  name: ButtonName,
  label: string,
  className: string,
  lamps: Array<{ el: HTMLElement; bit: number }>,
): HTMLElement {
  const cell = el('div', className);
  cell.dataset['button'] = name;
  const cap = el('span', 'tp-cap');
  cap.textContent = label;
  cell.append(cap);
  lamps.push({ el: cell, bit: bit(name) });
  return cell;
}

/** Nudge an element off its own centre without disturbing the centring. */
function offset(x: number, y: number): string {
  return `translate(calc(-50% + ${x.toFixed(1)}px), calc(-50% + ${y.toFixed(1)}px))`;
}
